// ---------------------------------------------------------------------------
// useDiscoveryData — Data fetching hook for FeltonDiscovery
// ---------------------------------------------------------------------------
// Encapsulates all useQuery calls (data + aggregations + trends),
// Load-More accumulators, chart transformations, and derived state.
//
// Pure data layer: receives parameters, returns computed results.
// All UI state (page, sort, trends metric) is owned by the caller.
// ---------------------------------------------------------------------------

import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { profilesService } from '@/services/v11/profilesService';
import { contentService } from '@/services/v11/contentService';
import { commentsService } from '@/services/v11/commentsService';
import { brandsService } from '@/services/v11/brandsService';
import { teamsService } from '@/services/v11/teamsService';
import { getFilterOptions } from '@/services/v11/filterOptionsService';
import { periodToGranularity } from '@/lib/periodToGranularity';
import type { TimePeriod } from '@/components/v11/discovery/types';
import {
  transformProfilesAggregationsToChartData,
  transformContentAggregationsToChartData,
  transformCommentsAggregationsToChartData,
  transformBrandsAggregationsToChartData,
  transformTeamsAggregationsToChartData,
} from '@/lib/transformAggregationsToChartData';
import { getColumnsForSource } from '@/data/explorerColumns';
import { MOCK_DATA_BY_SOURCE } from '@/data/mockExplorerData';
import { DATA_SOURCES } from '@/components/v11/discovery';
import type { DataSourceId } from '@/components/v11/discovery';
import { COLUMN_TO_SORT_FIELD_MAP, DEFAULT_SORT_BY_SOURCE, DEFAULT_SORT } from './discoveryConstants';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns a sort field that is valid for the given datasource. */
function safeSortBy(ds: DataSourceId, sort: string): string {
  const validFields = COLUMN_TO_SORT_FIELD_MAP[ds];
  if (validFields && Object.values(validFields).includes(sort)) return sort;
  return DEFAULT_SORT_BY_SOURCE[ds] || DEFAULT_SORT;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseDiscoveryDataOptions {
  readonly dataSource: DataSourceId;
  readonly period: string;
  readonly apiFilters: Record<string, unknown>;
  readonly currentPage: number;
  readonly pageSize: number;
  readonly sortBy: string;
  readonly sortOrder: 'asc' | 'desc';
  readonly isTrendsOpen: boolean;
  readonly trendsMetric: string;
  readonly trendsGroupBy: string;
  readonly customDateFrom?: string;
  readonly customDateTo?: string;
}

export interface UseDiscoveryDataResult {
  // Table data
  readonly currentData: ReadonlyArray<Record<string, unknown>>;
  readonly currentError: Error | null;
  readonly currentTotal: number | undefined;
  readonly hasMorePages: boolean;
  // Loading states
  readonly isInitialLoading: boolean;
  readonly isFetchingMore: boolean;
  readonly isFetchingCurrent: boolean;
  // Chart data (datasource-switched)
  readonly chartData: ReadonlyArray<Record<string, unknown>> | Record<string, unknown> | null;
  readonly isLoadingChart: boolean;
  readonly chartTotalRecords: number;
  // Trends
  readonly trendsData: ReadonlyArray<Record<string, unknown>>;
  readonly isLoadingTrends: boolean;
  readonly trendsError: Error | null;
  // Filter options (for dimension value resolution)
  readonly filterOptions: Record<string, unknown> | undefined;
  // Columns
  readonly currentColumns: ReadonlyArray<Record<string, unknown>>;
  readonly searchPlaceholder: string;
  // Granularity (derived from period)
  readonly granularity: string;
  // Aggregation summary (normalized across datasources)
  readonly aggregationSummary: AggregationSummary | null;
  readonly isLoadingAggregationSummary: boolean;
}

/** Normalized summary stats for the summary bar */
export interface AggregationSummaryStat {
  readonly label: string;
  readonly value: string;
}

export interface AggregationSummary {
  readonly stats: readonly AggregationSummaryStat[];
  readonly topPlatforms: readonly { name: string; percentage: number }[];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDiscoveryData(options: UseDiscoveryDataOptions): UseDiscoveryDataResult {
  const {
    dataSource,
    period,
    apiFilters,
    currentPage,
    pageSize,
    sortBy,
    sortOrder,
    isTrendsOpen,
    trendsMetric,
    trendsGroupBy,
    customDateFrom,
    customDateTo,
  } = options;

  // -------------------------------------------------------------------------
  // Internal accumulator state (Load More pagination)
  // -------------------------------------------------------------------------
  const [allLoadedProfiles, setAllLoadedProfiles] = useState<
    ReadonlyArray<Record<string, unknown>>
  >([]);
  const [allLoadedContent, setAllLoadedContent] = useState<ReadonlyArray<Record<string, unknown>>>(
    []
  );
  const [allLoadedComments, setAllLoadedComments] = useState<
    ReadonlyArray<Record<string, unknown>>
  >([]);
  const [allLoadedBrands, setAllLoadedBrands] = useState<ReadonlyArray<Record<string, unknown>>>(
    []
  );
  const [allLoadedTeams, setAllLoadedTeams] = useState<ReadonlyArray<Record<string, unknown>>>([]);

  // -------------------------------------------------------------------------
  // Render-phase accumulator reset (clears data before new fetch starts,
  // which lets the skeleton show during the transition).
  // This pattern is intentional — React supports setState during render
  // as an optimization to avoid an extra render cycle.
  // -------------------------------------------------------------------------
  const apiFiltersJson = JSON.stringify(apiFilters);
  const prevApiFiltersJsonRef = useRef(apiFiltersJson);
  if (apiFiltersJson !== prevApiFiltersJsonRef.current) {
    prevApiFiltersJsonRef.current = apiFiltersJson;
    if (allLoadedProfiles.length > 0) setAllLoadedProfiles([]);
    if (allLoadedContent.length > 0) setAllLoadedContent([]);
    if (allLoadedComments.length > 0) setAllLoadedComments([]);
    if (allLoadedBrands.length > 0) setAllLoadedBrands([]);
    if (allLoadedTeams.length > 0) setAllLoadedTeams([]);
  }

  // -------------------------------------------------------------------------
  // Filter options query
  // -------------------------------------------------------------------------
  const { data: filterOptions } = useQuery({
    queryKey: ['v11-filter-options'],
    queryFn: getFilterOptions,
    staleTime: 60 * 60 * 1000,
  });

  // -------------------------------------------------------------------------
  // Data queries (one per datasource, only active one runs)
  // -------------------------------------------------------------------------
  const queryDefaults = { staleTime: 5 * 60 * 1000, retry: 1, refetchOnMount: true as const };

  const {
    data: profilesApiData,
    isLoading: isLoadingProfiles,
    error: profilesError,
    isFetching: isFetchingProfiles,
    isPlaceholderData: isPlaceholderProfiles,
  } = useQuery({
    queryKey: [
      'v11-discovery',
      'profiles',
      period,
      apiFilters,
      currentPage,
      pageSize,
      safeSortBy('profiles', sortBy),
      sortOrder,
    ],
    queryFn: () =>
      profilesService.getProfiles({
        limit: pageSize,
        page: currentPage,
        sort: safeSortBy('profiles', sortBy),
        order: sortOrder,
        ...apiFilters,
      }),
    enabled: dataSource === 'profiles',
    placeholderData: prev => prev,
    ...queryDefaults,
  });

  const {
    data: contentApiData,
    isLoading: isLoadingContent,
    isFetching: isFetchingContent,
    error: contentError,
    isPlaceholderData: isPlaceholderContent,
  } = useQuery({
    queryKey: [
      'v11-discovery',
      'content',
      period,
      apiFilters,
      currentPage,
      pageSize,
      safeSortBy('content', sortBy),
      sortOrder,
    ],
    queryFn: () =>
      contentService.getContent({
        limit: pageSize,
        page: currentPage,
        sort: safeSortBy('content', sortBy),
        order: sortOrder,
        ...apiFilters,
      }),
    enabled: dataSource === 'content',
    placeholderData: prev => prev,
    ...queryDefaults,
  });

  const {
    data: commentsApiData,
    isLoading: isLoadingComments,
    isFetching: isFetchingComments,
    error: commentsError,
    isPlaceholderData: isPlaceholderComments,
  } = useQuery({
    queryKey: [
      'v11-discovery',
      'comments',
      period,
      apiFilters,
      currentPage,
      pageSize,
      safeSortBy('comments', sortBy),
      sortOrder,
    ],
    queryFn: () =>
      commentsService.getComments({
        limit: pageSize,
        page: currentPage,
        sort: safeSortBy('comments', sortBy),
        order: sortOrder,
        ...apiFilters,
      }),
    enabled: dataSource === 'comments',
    placeholderData: prev => prev,
    ...queryDefaults,
  });

  const {
    data: brandsApiData,
    isLoading: isLoadingBrands,
    isFetching: isFetchingBrands,
    error: brandsError,
    isPlaceholderData: isPlaceholderBrands,
  } = useQuery({
    queryKey: [
      'v11-discovery',
      'brands',
      period,
      apiFilters,
      currentPage,
      pageSize,
      safeSortBy('brands', sortBy),
      sortOrder,
    ],
    queryFn: () =>
      brandsService.getBrands({
        limit: pageSize,
        page: currentPage,
        sort: safeSortBy('brands', sortBy),
        order: sortOrder,
        ...apiFilters,
      }),
    enabled: dataSource === 'brands',
    placeholderData: prev => prev,
    ...queryDefaults,
  });

  const {
    data: teamsApiData,
    isLoading: isLoadingTeams,
    isFetching: isFetchingTeams,
    error: teamsError,
    isPlaceholderData: isPlaceholderTeams,
  } = useQuery({
    queryKey: [
      'v11-discovery',
      'teams',
      period,
      apiFilters,
      currentPage,
      pageSize,
      safeSortBy('teams', sortBy),
      sortOrder,
    ],
    queryFn: () =>
      teamsService.getTeams({
        limit: pageSize,
        page: currentPage,
        sort: safeSortBy('teams', sortBy),
        order: sortOrder,
        ...apiFilters,
      }),
    enabled: dataSource === 'teams',
    placeholderData: prev => prev,
    ...queryDefaults,
  });

  // -------------------------------------------------------------------------
  // Aggregation queries (one per datasource)
  // -------------------------------------------------------------------------
  const aggDefaults = { staleTime: 10 * 60 * 1000 };

  const { data: aggregationsData, isLoading: isLoadingAggregations } = useQuery({
    queryKey: ['v11-discovery-aggregations', period, apiFilters],
    queryFn: () => profilesService.getAggregations(apiFilters),
    enabled: dataSource === 'profiles',
    ...aggDefaults,
  });

  const { data: contentAggregationsData, isLoading: isLoadingContentAggregations } = useQuery({
    queryKey: ['v11-discovery-content-aggregations', period, apiFilters],
    queryFn: () => contentService.getAggregations(apiFilters),
    enabled: dataSource === 'content',
    ...aggDefaults,
  });

  const { data: commentsAggregationsData, isLoading: isLoadingCommentsAggregations } = useQuery({
    queryKey: ['v11-discovery-comments-aggregations', period, apiFilters],
    queryFn: () => commentsService.getAggregations(apiFilters),
    enabled: dataSource === 'comments',
    ...aggDefaults,
  });

  const { data: brandsAggregationsData, isLoading: isLoadingBrandsAggregations } = useQuery({
    queryKey: ['v11-discovery-brands-aggregations', period, apiFilters],
    queryFn: () => brandsService.getAggregations(apiFilters as any),
    enabled: dataSource === 'brands',
    ...aggDefaults,
  });

  const { data: teamsAggregationsData, isLoading: isLoadingTeamsAggregations } = useQuery({
    queryKey: ['v11-discovery-teams-aggregations', period, apiFilters],
    queryFn: () => teamsService.getAggregations(apiFilters as any),
    enabled: dataSource === 'teams',
    ...aggDefaults,
  });

  // -------------------------------------------------------------------------
  // Chart data transformations
  // -------------------------------------------------------------------------
  const chartDataFromAggregations = useMemo(
    () => (aggregationsData ? transformProfilesAggregationsToChartData(aggregationsData) : null),
    [aggregationsData]
  );
  const chartDataFromAggregationsContent = useMemo(
    () =>
      contentAggregationsData
        ? transformContentAggregationsToChartData(contentAggregationsData)
        : null,
    [contentAggregationsData]
  );
  const chartDataFromAggregationsComments = useMemo(
    () =>
      commentsAggregationsData
        ? transformCommentsAggregationsToChartData(commentsAggregationsData)
        : null,
    [commentsAggregationsData]
  );
  const chartDataFromAggregationsBrands = useMemo(
    () =>
      brandsAggregationsData
        ? transformBrandsAggregationsToChartData(brandsAggregationsData)
        : null,
    [brandsAggregationsData]
  );
  const chartDataFromAggregationsTeams = useMemo(
    () =>
      teamsAggregationsData ? transformTeamsAggregationsToChartData(teamsAggregationsData) : null,
    [teamsAggregationsData]
  );

  // -------------------------------------------------------------------------
  // Trends query
  // -------------------------------------------------------------------------
  const granularity = useMemo(
    () => periodToGranularity(period as TimePeriod, customDateFrom, customDateTo),
    [period, customDateFrom, customDateTo]
  );

  const trendsApiFilters = useMemo(
    () => ({ ...apiFilters, granularity }),
    [apiFilters, granularity]
  );

  const {
    data: trendsApiData,
    isLoading: isLoadingTrends,
    error: trendsError,
  } = useQuery({
    queryKey: ['v11-discovery-trends', dataSource, trendsMetric, trendsGroupBy, trendsApiFilters],
    queryFn: async () => {
      switch (dataSource) {
        case 'profiles':
          return await profilesService.getTrends(
            trendsMetric,
            trendsGroupBy,
            trendsApiFilters as any
          );
        case 'content':
          return await contentService.getTrends(
            trendsMetric,
            trendsGroupBy,
            trendsApiFilters as any
          );
        case 'comments':
          return await commentsService.getTrends(
            trendsMetric,
            trendsGroupBy,
            trendsApiFilters as any
          );
        case 'brands':
          return await brandsService.getTrends(
            trendsMetric,
            trendsGroupBy,
            trendsApiFilters as any
          );
        case 'teams':
          return await teamsService.getTrends(trendsMetric, trendsGroupBy, trendsApiFilters as any);
        default:
          return null;
      }
    },
    enabled: isTrendsOpen && dataSource !== 'hashtags',
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 2,
  });

  const trendsData = useMemo(() => {
    if (!trendsApiData?.data?.timeline) return [];
    return trendsApiData.data.timeline.map((point: any) => ({
      period: point.periodLabel || point.period,
      fullPeriod: point.period,
      ...point,
    }));
  }, [trendsApiData]);

  // -------------------------------------------------------------------------
  // Load More accumulator effects
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (dataSource === 'profiles' && profilesApiData?.data && !isPlaceholderProfiles) {
      if (currentPage === 1) {
        setAllLoadedProfiles(profilesApiData.data as Record<string, unknown>[]);
      } else {
        setAllLoadedProfiles(prev => [
          ...prev,
          ...(profilesApiData.data as Record<string, unknown>[]),
        ]);
      }
    }
  }, [profilesApiData, currentPage, dataSource, isPlaceholderProfiles]);

  useEffect(() => {
    if (dataSource === 'content' && contentApiData?.data && !isPlaceholderContent) {
      if (currentPage === 1) {
        setAllLoadedContent(contentApiData.data as Record<string, unknown>[]);
      } else {
        setAllLoadedContent(prev => [
          ...prev,
          ...(contentApiData.data as Record<string, unknown>[]),
        ]);
      }
    }
  }, [contentApiData, currentPage, dataSource, isPlaceholderContent]);

  useEffect(() => {
    if (dataSource === 'comments' && commentsApiData?.data && !isPlaceholderComments) {
      if (currentPage === 1) {
        setAllLoadedComments(commentsApiData.data as Record<string, unknown>[]);
      } else {
        setAllLoadedComments(prev => [
          ...prev,
          ...(commentsApiData.data as Record<string, unknown>[]),
        ]);
      }
    }
  }, [commentsApiData, currentPage, dataSource, isPlaceholderComments]);

  useEffect(() => {
    if (dataSource === 'brands' && brandsApiData?.data && !isPlaceholderBrands) {
      if (currentPage === 1) {
        setAllLoadedBrands(brandsApiData.data as Record<string, unknown>[]);
      } else {
        setAllLoadedBrands(prev => [...prev, ...(brandsApiData.data as Record<string, unknown>[])]);
      }
    }
  }, [brandsApiData, currentPage, dataSource, isPlaceholderBrands]);

  useEffect(() => {
    if (dataSource === 'teams' && teamsApiData?.data && !isPlaceholderTeams) {
      if (currentPage === 1) {
        setAllLoadedTeams(teamsApiData.data as Record<string, unknown>[]);
      } else {
        setAllLoadedTeams(prev => [...prev, ...(teamsApiData.data as Record<string, unknown>[])]);
      }
    }
  }, [teamsApiData, currentPage, dataSource, isPlaceholderTeams]);

  // -------------------------------------------------------------------------
  // Derived state (datasource-switched)
  // -------------------------------------------------------------------------
  const currentData = useMemo(() => {
    if (dataSource === 'profiles') {
      // For multi-page accumulation (Load More), use accumulated data
      if (currentPage > 1 && allLoadedProfiles.length > 0) return allLoadedProfiles;
      // On page 1, prefer fresh API data to stay consistent with meta.total
      if (!isPlaceholderProfiles && profilesApiData?.data)
        return profilesApiData.data as ReadonlyArray<Record<string, unknown>>;
      return [];
    }
    if (dataSource === 'content') {
      if (currentPage > 1 && allLoadedContent.length > 0) return allLoadedContent;
      if (!isPlaceholderContent && contentApiData?.data)
        return contentApiData.data as ReadonlyArray<Record<string, unknown>>;
      return [];
    }
    if (dataSource === 'comments') {
      if (currentPage > 1 && allLoadedComments.length > 0) return allLoadedComments;
      if (!isPlaceholderComments && commentsApiData?.data)
        return commentsApiData.data as ReadonlyArray<Record<string, unknown>>;
      return [];
    }
    if (dataSource === 'brands') {
      if (currentPage > 1 && allLoadedBrands.length > 0) return allLoadedBrands;
      if (!isPlaceholderBrands && brandsApiData?.data)
        return brandsApiData.data as ReadonlyArray<Record<string, unknown>>;
      return [];
    }
    if (dataSource === 'teams') {
      if (currentPage > 1 && allLoadedTeams.length > 0) return allLoadedTeams;
      if (!isPlaceholderTeams && teamsApiData?.data)
        return teamsApiData.data as ReadonlyArray<Record<string, unknown>>;
      return [];
    }
    return MOCK_DATA_BY_SOURCE[dataSource] as Record<string, unknown>[];
  }, [
    dataSource,
    currentPage,
    allLoadedProfiles,
    allLoadedContent,
    allLoadedComments,
    allLoadedBrands,
    allLoadedTeams,
    profilesApiData,
    isPlaceholderProfiles,
    contentApiData,
    isPlaceholderContent,
    commentsApiData,
    isPlaceholderComments,
    brandsApiData,
    isPlaceholderBrands,
    teamsApiData,
    isPlaceholderTeams,
  ]);

  const currentError = useMemo(() => {
    switch (dataSource) {
      case 'profiles':
        return profilesError;
      case 'content':
        return contentError;
      case 'comments':
        return commentsError;
      case 'brands':
        return brandsError;
      case 'teams':
        return teamsError;
      default:
        return null;
    }
  }, [dataSource, profilesError, contentError, commentsError, brandsError, teamsError]);

  const isInitialLoading = useMemo(() => {
    switch (dataSource) {
      case 'profiles':
        return isLoadingProfiles || (isFetchingProfiles && allLoadedProfiles.length === 0);
      case 'content':
        return isLoadingContent || (isFetchingContent && allLoadedContent.length === 0);
      case 'comments':
        return isLoadingComments || (isFetchingComments && allLoadedComments.length === 0);
      case 'brands':
        return isLoadingBrands || (isFetchingBrands && allLoadedBrands.length === 0);
      case 'teams':
        return isLoadingTeams || (isFetchingTeams && allLoadedTeams.length === 0);
      default:
        return false;
    }
  }, [
    dataSource,
    isLoadingProfiles,
    isFetchingProfiles,
    allLoadedProfiles.length,
    isLoadingContent,
    isFetchingContent,
    allLoadedContent.length,
    isLoadingComments,
    isFetchingComments,
    allLoadedComments.length,
    isLoadingBrands,
    isFetchingBrands,
    allLoadedBrands.length,
    isLoadingTeams,
    isFetchingTeams,
    allLoadedTeams.length,
  ]);

  const isFetchingMore = useMemo(() => {
    switch (dataSource) {
      case 'profiles':
        return isFetchingProfiles && allLoadedProfiles.length > 0;
      case 'content':
        return isFetchingContent && allLoadedContent.length > 0;
      case 'comments':
        return isFetchingComments && allLoadedComments.length > 0;
      case 'brands':
        return isFetchingBrands && allLoadedBrands.length > 0;
      case 'teams':
        return isFetchingTeams && allLoadedTeams.length > 0;
      default:
        return false;
    }
  }, [
    dataSource,
    isFetchingProfiles,
    allLoadedProfiles.length,
    isFetchingContent,
    allLoadedContent.length,
    isFetchingComments,
    allLoadedComments.length,
    isFetchingBrands,
    allLoadedBrands.length,
    isFetchingTeams,
    allLoadedTeams.length,
  ]);

  const isFetchingCurrent = useMemo(() => {
    switch (dataSource) {
      case 'profiles':
        return isFetchingProfiles;
      case 'content':
        return isFetchingContent;
      case 'comments':
        return isFetchingComments;
      case 'brands':
        return isFetchingBrands;
      case 'teams':
        return isFetchingTeams;
      default:
        return false;
    }
  }, [
    dataSource,
    isFetchingProfiles,
    isFetchingContent,
    isFetchingComments,
    isFetchingBrands,
    isFetchingTeams,
  ]);

  const currentTotal = useMemo(() => {
    switch (dataSource) {
      case 'profiles':
        return profilesApiData?.meta?.total;
      case 'content':
        return contentApiData?.meta?.total;
      case 'comments':
        return commentsApiData?.meta?.total;
      case 'brands':
        return brandsApiData?.meta?.total;
      case 'teams':
        return teamsApiData?.meta?.total;
      default:
        return undefined;
    }
  }, [
    dataSource,
    profilesApiData?.meta?.total,
    contentApiData?.meta?.total,
    commentsApiData?.meta?.total,
    brandsApiData?.meta?.total,
    teamsApiData?.meta?.total,
  ]);

  const hasMorePages = useMemo(() => {
    switch (dataSource) {
      case 'profiles':
        return profilesApiData?.meta?.hasMorePages ?? false;
      case 'content':
        return contentApiData?.meta?.hasMorePages ?? false;
      case 'comments':
        return commentsApiData?.meta?.hasMorePages ?? false;
      case 'brands':
        return brandsApiData?.meta?.hasMorePages ?? false;
      case 'teams':
        return teamsApiData?.meta?.hasMorePages ?? false;
      default:
        return false;
    }
  }, [
    dataSource,
    profilesApiData?.meta?.hasMorePages,
    contentApiData?.meta?.hasMorePages,
    commentsApiData?.meta?.hasMorePages,
    brandsApiData?.meta?.hasMorePages,
    teamsApiData?.meta?.hasMorePages,
  ]);

  // -------------------------------------------------------------------------
  // Chart view derived state
  // -------------------------------------------------------------------------
  const chartData = useMemo(() => {
    if (dataSource === 'profiles' && chartDataFromAggregations) return chartDataFromAggregations;
    if (dataSource === 'content' && chartDataFromAggregationsContent)
      return chartDataFromAggregationsContent;
    if (dataSource === 'comments' && chartDataFromAggregationsComments)
      return chartDataFromAggregationsComments;
    if (dataSource === 'brands' && chartDataFromAggregationsBrands)
      return chartDataFromAggregationsBrands;
    if (dataSource === 'teams' && chartDataFromAggregationsTeams)
      return chartDataFromAggregationsTeams;
    return currentData;
  }, [
    dataSource,
    chartDataFromAggregations,
    chartDataFromAggregationsContent,
    chartDataFromAggregationsComments,
    chartDataFromAggregationsBrands,
    chartDataFromAggregationsTeams,
    currentData,
  ]);

  const isLoadingChart = useMemo(() => {
    switch (dataSource) {
      case 'profiles':
        return isLoadingAggregations;
      case 'content':
        return isLoadingContentAggregations;
      case 'comments':
        return isLoadingCommentsAggregations;
      case 'brands':
        return isLoadingBrandsAggregations;
      case 'teams':
        return isLoadingTeamsAggregations;
      default:
        return false;
    }
  }, [
    dataSource,
    isLoadingAggregations,
    isLoadingContentAggregations,
    isLoadingCommentsAggregations,
    isLoadingBrandsAggregations,
    isLoadingTeamsAggregations,
  ]);

  const chartTotalRecords = useMemo(() => {
    // Only use API total when we have non-placeholder data with a valid meta response.
    // Placeholder data may be from a previous query with different filters, and
    // cancelled requests can leave meta as undefined (httpClient returns {} on cancel).
    if (dataSource === 'profiles') {
      if (isPlaceholderProfiles || !profilesApiData?.meta) return currentData.length || 0;
      return profilesApiData.meta.total ?? currentData.length;
    }
    if (dataSource === 'content') {
      if (isPlaceholderContent || !contentApiData?.meta) return currentData.length || 0;
      return contentApiData.meta.total ?? currentData.length;
    }
    if (dataSource === 'comments') {
      if (isPlaceholderComments || !commentsApiData?.meta) return currentData.length || 0;
      return commentsApiData.meta.total ?? currentData.length;
    }
    if (dataSource === 'brands') {
      if (isPlaceholderBrands || !brandsApiData?.meta) return currentData.length || 0;
      return brandsApiData.meta.total ?? currentData.length;
    }
    if (dataSource === 'teams') {
      if (isPlaceholderTeams || !teamsApiData?.meta) return currentData.length || 0;
      return teamsApiData.meta.total ?? currentData.length;
    }
    return currentData.length;
  }, [
    dataSource,
    profilesApiData,
    contentApiData,
    commentsApiData,
    brandsApiData,
    teamsApiData,
    isPlaceholderProfiles,
    isPlaceholderContent,
    isPlaceholderComments,
    isPlaceholderBrands,
    isPlaceholderTeams,
    currentData.length,
  ]);

  // -------------------------------------------------------------------------
  // Columns and search placeholder
  // -------------------------------------------------------------------------
  const currentColumns = useMemo(() => getColumnsForSource(dataSource), [dataSource]);

  const searchPlaceholder = useMemo(() => {
    const source = DATA_SOURCES.find(s => s.id === dataSource);
    return source?.searchPlaceholder ?? 'Search...';
  }, [dataSource]);

  // -------------------------------------------------------------------------
  // Aggregation summary (normalized across datasources)
  // -------------------------------------------------------------------------

  const aggregationSummary = useMemo((): AggregationSummary | null => {
    const fmt = (n: number) =>
      new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
    const computePlatforms = (
      platforms: readonly { name: string; count: number }[]
    ): AggregationSummary['topPlatforms'] => {
      const total = platforms.reduce((sum, p) => sum + p.count, 0);
      if (total === 0) return [];
      return platforms
        .filter(p => p.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 4)
        .map(p => ({ name: p.name, percentage: Math.round((p.count / total) * 100) }));
    };

    if (dataSource === 'profiles' && aggregationsData?.data) {
      const d = aggregationsData.data;
      return {
        stats: [
          { label: 'Profiles', value: fmt(d.totals.profiles) },
          { label: 'Followers', value: fmt(d.totals.followers) },
          { label: 'Avg Followers', value: fmt(d.totals.avgFollowers) },
          { label: 'Avg Sentiment', value: d.totals.avgSentiment.toFixed(1) },
        ],
        topPlatforms: computePlatforms(d.platforms),
      };
    }
    if (dataSource === 'content' && contentAggregationsData?.data) {
      const d = contentAggregationsData.data;
      return {
        stats: [
          { label: 'Posts', value: fmt(d.totals.posts) },
          { label: 'Likes', value: fmt(d.totals.likes) },
          { label: 'Comments', value: fmt(d.totals.comments) },
          { label: 'Avg Engagement', value: `${(d.totals.avgEngagement ?? 0).toFixed(1)}%` },
        ],
        topPlatforms: computePlatforms(d.platforms),
      };
    }
    if (dataSource === 'comments' && commentsAggregationsData?.data) {
      const d = commentsAggregationsData.data;
      return {
        stats: [
          { label: 'Comments', value: fmt(d.totals.comments) },
          { label: 'Avg Sentiment', value: (d.totals.avgSentiment ?? 0).toFixed(1) },
          // { label: 'Toxic Rate', value: `${((d.totals.toxicRate ?? 0) * 100).toFixed(1)}%` },
          // { label: 'Spam Rate', value: `${((d.totals.spamRate ?? 0) * 100).toFixed(1)}%` },
        ],
        topPlatforms: computePlatforms(d.platforms),
      };
    }
    if (dataSource === 'brands' && brandsAggregationsData?.data) {
      const d = brandsAggregationsData.data;
      return {
        stats: [
          { label: 'Brands', value: fmt(d.totals.brands) },
          { label: 'Profiles', value: fmt(d.totals.profiles) },
          // { label: 'Detections', value: fmt(d.totals.totalDetections) },
        ],
        topPlatforms: [],
      };
    }
    if (dataSource === 'teams' && teamsAggregationsData?.data) {
      const d = teamsAggregationsData.data;
      return {
        stats: [
          { label: 'Teams', value: fmt(d.totals.teams) },
          { label: 'Profiles', value: fmt(d.totals.profiles) },
          // { label: 'Mentions', value: fmt(d.totals.totalMentions) },
        ],
        topPlatforms: [],
      };
    }
    return null;
  }, [
    dataSource,
    aggregationsData,
    contentAggregationsData,
    commentsAggregationsData,
    brandsAggregationsData,
    teamsAggregationsData,
  ]);

  const isLoadingAggregationSummary = isLoadingChart;

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------
  return {
    currentData,
    currentError,
    currentTotal,
    hasMorePages,
    isInitialLoading,
    isFetchingMore,
    isFetchingCurrent,
    chartData: chartData as any,
    isLoadingChart,
    chartTotalRecords,
    trendsData,
    isLoadingTrends,
    trendsError,
    filterOptions: filterOptions as Record<string, unknown> | undefined,
    currentColumns: currentColumns as any,
    searchPlaceholder,
    granularity,
    aggregationSummary,
    isLoadingAggregationSummary,
  };
}
